import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Box, Flex, IconButton, Image, Input, Button, Table, Thead, Tbody, Tr, Th, Td, Text, useToast, Spinner, Badge } from "@chakra-ui/react";
import { FaArrowLeft, FaUpload } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { buildLogoCandidates, nextLogoCandidate, uploadSupplierLogo, validateLogoFile, LOGO_ACCEPT, LOGO_MAX_BYTES } from "../../../tools/supplierLogo";

// Add or replace the logo of an ALREADY-CREATED supplier. AddUser covers the
// new-supplier case; this covers every existing one, including suppliers whose
// owning user has since been deleted (logos key on suppliercode, not userid).
const SupplierLogos = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [uploadingCode, setUploadingCode] = useState(null);
  // Bumped per code after a successful upload to defeat the 5-min logo cache.
  const [versions, setVersions] = useState({});
  const fileInputs = useRef({});

  const fetchSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/get-suppliers-and-pages`);
      const rows = response.data?.data?.suppliers || [];
      setSuppliers([...rows].sort((a, b) => (a.suppliercode || "").localeCompare(b.suppliercode || "")));
    } catch (error) {
      toast({ title: "Failed to load suppliers", description: error?.response?.data?.message || "Could not reach the API.", status: "error", duration: 6000, isClosable: true });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  const handlePick = (suppliercode) => {
    fileInputs.current[suppliercode]?.click();
  };

  const handleFile = async (suppliercode, e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file after a failure
    if (!file) return;

    const problem = validateLogoFile(file);
    if (problem) {
      toast({ title: "Logo not accepted", description: problem, status: "warning", duration: 5000, isClosable: true });
      return;
    }

    try {
      setUploadingCode(suppliercode);
      const result = await uploadSupplierLogo(file, suppliercode);
      setVersions((prev) => ({ ...prev, [suppliercode]: (prev[suppliercode] || 0) + 1 }));
      toast({ title: result.title || "Logo uploaded", description: result.message, status: "success", duration: 4000, isClosable: true });
    } catch (error) {
      toast({ title: "Upload failed", description: error?.response?.data?.message || "The logo could not be saved.", status: "error", duration: 8000, isClosable: true });
    } finally {
      setUploadingCode(null);
    }
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return suppliers;
    return suppliers.filter((s) => `${s.suppliercode} ${s.fname}`.toLowerCase().includes(term));
  }, [suppliers, search]);

  return (
    <Box overflowX="auto" mt={16}>
      <Flex justify="space-between" mb={4} align="center" gap={4}>
        <Flex align="center" gap={4}>
          <IconButton icon={<FaArrowLeft />} colorScheme="teal" onClick={() => navigate("/users")} aria-label="Back to users" />
          <Text fontWeight="semibold">Supplier Logos</Text>
        </Flex>
        <Input placeholder="Search supplier" value={search} onChange={(e) => setSearch(e.target.value)} width="260px" size="sm" />
      </Flex>

      <Text fontSize="sm" color="gray.500" mb={3}>
        JPG, PNG or WEBP, max {LOGO_MAX_BYTES / 1024 / 1024}MB. Saved as the supplier code and applied immediately — no rebuild or redeploy.
      </Text>

      {loading ? (
        <Flex justify="center" py={10}>
          <Spinner />
        </Flex>
      ) : (
        <Box maxH="calc(100vh - 260px)" overflowY="auto" border="1px" borderColor="gray.200" borderRadius="md">
          <Table variant="striped" size="sm" colorScheme="custom">
            <Thead position="sticky" top={0} bg="white" zIndex={1}>
              <Tr>
                <Th width="80px">Logo</Th>
                <Th>Supplier Code</Th>
                <Th>Display Name</Th>
                <Th width="160px">Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filtered.map((supplier) => {
                const code = supplier.suppliercode;
                // Once uploaded in this session, ask the API first so the admin
                // sees the file that was actually just written.
                const candidates = buildLogoCandidates(code, {cacheBust: versions[code], preferApi: Boolean(versions[code])});
                return (
                  <Tr key={code}>
                    <Td>
                      <Image
                        boxSize="44px"
                        borderRadius="full"
                        objectFit="contain"
                        border="1px solid"
                        borderColor="gray.200"
                        bg="white"
                        src={candidates[0]}
                        onError={({ currentTarget }) => nextLogoCandidate(currentTarget, candidates)}
                        alt={`${code} logo`}
                      />
                    </Td>
                    <Td fontWeight="medium">{code}</Td>
                    <Td>
                      {supplier.fname}
                      {supplier.activeyn === false && (
                        <Badge ml={2} colorScheme="gray">
                          inactive
                        </Badge>
                      )}
                    </Td>
                    <Td>
                      <input type="file" accept={LOGO_ACCEPT} style={{ display: "none" }} ref={(el) => (fileInputs.current[code] = el)} onChange={(e) => handleFile(code, e)} />
                      <Button size="xs" leftIcon={<FaUpload />} colorScheme="blue" variant="outline" isLoading={uploadingCode === code} onClick={() => handlePick(code)}>
                        {versions[code] ? "Replace again" : "Upload / Replace"}
                      </Button>
                    </Td>
                  </Tr>
                );
              })}
              {filtered.length === 0 && (
                <Tr>
                  <Td colSpan={4}>
                    <Text py={4} textAlign="center" color="gray.500">
                      No suppliers match "{search}".
                    </Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      )}

      <Text fontSize="xs" color="gray.500" mt={3}>
        Showing {filtered.length} of {suppliers.length} suppliers.
      </Text>
    </Box>
  );
};

export default SupplierLogos;
